import { useEffect } from 'react';
import { useStore } from './store/useStore';
import { useKeyboard } from './hooks/useKeyboard';
import { Desktop } from './components/Desktop/Desktop';
import { Landing } from './components/Shared/Landing';
import { PrivacyPolicy } from './components/Shared/PrivacyPolicy';
import { CommandPalette } from './components/Shared/CommandPalette';
import { NotificationToasts } from './components/Shared/NotificationToasts';
import { AppLauncher } from './components/Shared/AppLauncher';
import { Loader } from './components/Shared/Loader';

export default function App() {
  const authenticated = useStore((s) => s.authenticated);
  const authChecking = useStore((s) => s.authChecking);
  const checkAuth = useStore((s) => s.checkAuth);
  const addNotification = useStore((s) => s.addNotification);
  const theme = useStore((s) => s.theme);
  const commandPaletteOpen = useStore((s) => s.commandPaletteOpen);
  const appLauncherOpen = useStore((s) => s.appLauncherOpen);

  useKeyboard();

  // Keep-alive: while a user is actively using the desktop, ping the backend
  // so Render's free instance doesn't spin down mid-session (which would kill
  // running terminals and dev servers).
  useEffect(() => {
    if (!authenticated) return;
    const ping = () => {
      fetch('/api/health', { credentials: 'include' }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 4 * 60 * 1000); // well under the 15-min idle limit
    return () => clearInterval(id);
  }, [authenticated]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');

    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error') === 'forbidden') {
      addNotification('Access denied', 'This Google account is not allowed to sign in.', 'error');
    } else if (params.get('auth_error') === 'redirect_mismatch') {
      addNotification(
        'Google setup needed',
        `Add this exact redirect URI in Google Console → Credentials → your OAuth client: ${params.get('uri') || '(check Render env GOOGLE_REDIRECT_URI)'}`,
        'error'
      );
    } else if (params.get('google_auth') === 'success') {
      addNotification('Connected', 'Google Drive connected.', 'success');
    }
    if (params.get('auth_error') || params.get('google_auth')) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    checkAuth();
  }, [checkAuth, addNotification, theme]);

  // Public pages (no auth): privacy policy + landing with app purpose —
  // required by Google OAuth verification. Checked before auth so visitors
  // never see a login loader on them.
  const path = window.location.pathname;
  if (path === '/privacy') {
    return <PrivacyPolicy />;
  }

  if (authChecking) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-desktop-bg">
        <Loader size="lg" text="Loading Shadow Cloud Code..." />
      </div>
    );
  }

  return (
    <>
      {authenticated ? (
        <Desktop />
      ) : (
        <Landing />
      )}
      {commandPaletteOpen && <CommandPalette />}
      {appLauncherOpen && <AppLauncher />}
      <NotificationToasts />
    </>
  );
}